import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Button, Stack } from "react-bootstrap";
import Avatar from "../UI/Avatar";

/**
 * Props interface for the ImageSelector component
 */
interface ImageSelectorProps {
  /** URL of the current image */
  imageUrl?: string;
  /** Callback function when image is selected */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Callback function when image is deleted */
  onDeleted: () => void;
  /** Alt text for the image */
  altStr?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
}

/**
 * ImageSelector component allows users to select, display, and delete an image
 *
 * @param {ImageSelectorProps} props - Component props
 * @returns {JSX.Element} ImageSelector component
 */
const ImageSelector = ({
  imageUrl,
  onChange,
  onDeleted,
  altStr = "Profile Image",
  disabled = false,
}: ImageSelectorProps): JSX.Element => {
  const inputFile = useRef<HTMLInputElement>(null);
  const [displayImg, setDisplayImg] = useState<string>(
    "/images/organization-avatar.png"
  );
  const [imgLocal, setImgLocal] = useState<boolean>(false);

  useEffect(() => {
    setDisplayImg(imageUrl || "/images/organization-avatar.png");
  }, [imageUrl]);

  const onBrowseClick = (): void => {
    if (inputFile.current) {
      inputFile.current.click();
    }
  };

  const onDeleteClick = (): void => {
    console.log("Delete image", inputFile.current, inputFile.current?.value);
    if (inputFile.current) {
      inputFile.current.value = "";
    }
    onDeleted();
    setImgLocal(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files.length > 0) {
      onChange(event);
      setImgLocal(true);
    }
  };

  return (
    <Stack
      className="gap-3 sm:gap-[12px] items-center sm:items-end"
      direction="horizontal"
    >
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-[12px]">
        <Button
          variant="link-2"
          id="Browse"
          type="button"
          onClick={onBrowseClick}
          disabled={disabled}
          aria-label="Browse for image"
        >
          Browse
        </Button>
        <input
          type="file"
          id="pictureSelector"
          name="ProfilePicture"
          ref={inputFile}
          accept="image/png, image/jpeg, image/jpg, image/gif"
          onChange={handleFileChange}
          style={{ display: "none" }}
          aria-hidden="true"
        />
        <Button
          variant="link-2"
          id="Delete"
          type="button"
          onClick={onDeleteClick}
          disabled={disabled || !imgLocal}
          aria-label="Delete image"
        >
          Delete
        </Button>
      </div>
      <Avatar imageUrl={displayImg} altText={altStr} size="md" />
    </Stack>
  );
};

export default ImageSelector;
